/**
 * Legacy bucketplace/ui에서 필요한 것들만 따로 옮겨두었습니다.
 * 새로만든 Sticky를 기획전에 적용할 수 있으면 제거하면 됩니다.
 */

import React, { useCallback, useMemo, useRef, useState } from 'react';

import { StickyControlContext, StickyValueContext, StickyValueContextValue, StickyValueEntry, StickyValueEntryState } from '../contexts';
import { StickyDirection, StickyOptions, StickyState } from '../types';

const STICKY_ENTRY_STATE_KEYS: (keyof StickyValueEntryState)[] = [
  'disabled',
  'emulatedDisabled',
  'height',
  'bottom',
  'beginY',
];
const DIRECTIONS: StickyDirection[] = ['top', 'bottom'];

type StickyProviderEntryRecord = Record<string, StickyValueEntry>;

interface StickyEntriesOrder {
  ids: string[];
  parentIndices: number[];
}

export interface StickyProviderProps {
  children: React.ReactNode;
}

export function StickyProvider(
  props: StickyProviderProps,
): React.ReactElement {
  const { children } = props;
  const [entries, setEntries] = useState<StickyProviderEntryRecord>({});
  const entriesRef = useRef<StickyProviderEntryRecord>(entries);
  entriesRef.current = entries;
  const shouldSortEntriesRef = useRef<boolean>(false);
  const entriesOrderRef = useRef<Record<StickyDirection, StickyEntriesOrder>>({
    top: { ids: [], parentIndices: [] },
    bottom: { ids: [], parentIndices: [] },
  });
  const updateEntries = useCallback((
    getState: (
      prevState: StickyProviderEntryRecord,
    ) => StickyProviderEntryRecord,
  ) => {
    return setEntries((prevState) => {
      // top/bottom sticky objects are unrelated with each other. Therefore
      // run entire routine for each direction.
      const nextState = getState(prevState);
      if (nextState === prevState) {
        return prevState;
      }
      const entriesMap = nextState;
      const resultSet: Record<string, StickyValueEntry> =
        { ...nextState };
      DIRECTIONS.forEach((direction) => {
        const orders = entriesOrderRef.current[direction];
        if (shouldSortEntriesRef.current) {
          // Sort the sticky entries according to the DOM order.
          // ... This can be done relatively easily IF we had shared className,
          // or attribute available to search using querySelectorAll().
          // However, we can't determine that's the case - this can be provided
          // with HTMLElement - no other requirements are specified - therefore
          // we have to handle every case.
          // Therefore... we must implement graph traversal.
          // - The sorting algorithm doesn't need to be implemented, as
          //   array.sort() can receive comparator function.
          // - While sorthing through array.sort(), comparator function gets
          //   called O(n log n) times. Since we can assume that sticky nodes
          //   exist on the screen AT MOST 10, this is pretty much negligble.
          // - The comparator function should just call
          //   'compareDocumentPosition()' method to compare the position
          //   between two elements.
          // Note that .sort() method is mutable.
          // NOTE: This routine will not work properly if the sticky element is
          // between multiple document, for example, iframes, or window.open().
          // Since we can assume that this will not happen, we can ignore this.
          // However this is pretty trivial to resolve since we just need to
          // attach new StickyProvider onto the new document's elements.
          const dirEntries = Object.keys(entriesMap)
            .map((key) => entriesMap[key])
            .filter((item) => item.direction === direction);
          const orderedItems = dirEntries.sort((a, b) => {
            const aElem = a.ref.current;
            const bElem = b.ref.current;
            if (aElem == null) { return 1; }
            if (bElem == null) { return -1; }
            const compareResult = aElem.compareDocumentPosition(bElem);
            if (compareResult & Node.DOCUMENT_POSITION_FOLLOWING) {
              return -1;
            }
            if (compareResult & Node.DOCUMENT_POSITION_PRECEDING) {
              return 1;
            }
            return 0;
          });
          let ids = orderedItems.map((v) => v.id);
          // Flip the list if the direction is bottom.
          if (direction === 'bottom') {
            ids = ids.reverse();
          }
          // Determine the Sticky dependency graph parent indices. Since the
          // 'orders.ids' already stores sorted IDs, we can use that to quickly
          // derive parent indices.

          // NOTE: While it should be possible to determine parent IDs and order
          // at the same time, the logic is already mind-boggling at this
          // point. To avoid making this difficult, sorting -> backreferencing
          // can be applied to quickly determine the parents of the Sticky node.
          const parentIndices: number[] = [];
          for (let i = 0; i < ids.length; i += 1) {
            const item = entriesMap[ids[i]];
            if (item == null) { continue; }
            const itemElem = item.ref.current;
            if (itemElem == null) { continue; }
            // For each node, determine the "parent" node, excluding the
            // siblings.
            // Due to this nested loop, the time complexity would be
            // O(n^2 log m). Still, this is acceptable as n is already too
            // small.
            let targetIndex = -1;
            for (let j = i - 1; j >= 0; j -= 1) {
              const target = entriesMap[ids[j]];
              if (target == null) { continue; }
              const targetElem = target.ref.current;
              if (targetElem == null) { continue; }
              const targetParentElem = targetElem.parentElement;
              if (targetParentElem == null) { continue; }
              // Check if the target:
              // - "has no bottom" therefore it should always stick regardless
              //   of DOM node hierarchy
              // - 's parent element contains itemElem. When this is true,
              //   target element is on the top/bottom of the item element -
              //   which means that the offset should use data from there.
              //
              // Let's skip "disableBottom" for now - the "contains" logic
              // works like this:
              //
              // 1. In the following case, B should be below A since
              //    A will always be visible while looking at B.
              // div
              //  +------+------------+
              //        A             B
              //
              // 2. It doesn't matter if B is below some node too.
              // div
              //  +------+------------+
              //         A           div
              //                      +--------+
              //                               B
              //
              // 3. HOWEVER, if A is below some node, it should not stick
              //    with each other, as A and B will never intersect, since
              //    the Sticky's scope only expand's to its parent element.
              //
              // div
              //  +------+------------+
              //        div           B
              //         +
              //         A
              //
              // For these cases, A.parentElement.contains(B) should check
              // all these (as the nodes are already ordered at this point)
              // and return the correct result. Therefore, we're just
              // only relying on this logic.

              // NOTE: This WILL break if the code uses
              // flex-direction: row-reverse or something like that. We hope
              // that will never happen...
              if (
                target.disableBottom ||
                targetParentElem.contains(itemElem)
              ) {
                // Found an adequate item, set the index and break
                targetIndex = j;
                break;
              }
            }
            parentIndices[i] = targetIndex;
          }
          orders.ids = ids;
          orders.parentIndices = parentIndices;
          entriesOrderRef.current[direction] = orders;
        }
        // Determine the new accumulatedOffset...
        const accumulatedOffsets: number[] = [];
        const accumulatedOffsetRanges: [number, number][] = [];
        orders.ids.forEach((key, order) => {
          const parentIndex = orders.parentIndices[order];
          const parentOffset =
            parentIndex === -1 ? 0 : accumulatedOffsets[parentIndex] ?? 0;
          const parentOffsetRange: [number, number] = parentIndex === -1
            ? [0, 0]
            : accumulatedOffsetRanges[parentIndex] ?? [0, 0];
          const item = entriesMap[key];
          if (item == null || parentIndex == null) { return; }
          if (
            item.accumulatedOffset !== parentOffset ||
            item.order !== order
          ) {
            resultSet[key] = {
              ...item,
              accumulatedOffset: parentOffset,
              accumulatedOffsetRange: parentOffsetRange,
              order,
            };
          }
          if (!item.disabled && !item.bottom) {
            accumulatedOffsets[order] =
              parentOffset + item.offset + (item.height ?? 0);
            accumulatedOffsetRanges[order] = [
              parentOffsetRange[0] + (item.height ?? 0) +
                (item.offsetRange?.[0] ?? item.offset),
              parentOffsetRange[1] + (item.height ?? 0) +
                (item.offsetRange?.[1] ?? item.offset),
            ];
          } else {
            accumulatedOffsets[order] = parentOffset;
            accumulatedOffsetRanges[order] = parentOffsetRange;
          }
        });
      });
      shouldSortEntriesRef.current = false;
      return resultSet;
    });
  }, []);
  const handleRegister = useCallback((): string => {
    shouldSortEntriesRef.current = true;
    // Assign a random ID
    // NOTE: This is NOT deterministic and therefore there can be
    // inconsistencies between the server and the client while doing SSR.
    // However, this ID is never exposed outside React state and therefore
    // there won't be any discrepancies in the resulting HTML itself.
    return String((Math.random() * 0x7FFFFFFF) | 0);
  }, [setEntries]);
  const handleUpdate = useCallback((
    id: string,
    containerRef: React.RefObject<HTMLElement | null>,
    options: StickyOptions,
    state: StickyState,
  ): void => {
    updateEntries((prevState) => {
      // Check if the related state has changed so that we have to recalculate
      // the accumulatedOffset of sibling states.
      let prevItem = prevState[id];
      if (prevItem == null) {
        // New item has appended
        shouldSortEntriesRef.current = true;
        prevItem = {
          id,
          type: undefined,
          shouldInfluenceScroll: true,
          direction: null,
          disabled: true,
          emulatedDisabled: true,
          disableBottom: false,
          bottom: false,
          height: 0,
          offset: 0,
          accumulatedOffset: 0,
          accumulatedOffsetRange: [0, 0],
          beginY: 0,
          ref: containerRef,
          order: 0,
        };
      }
      const hasChanged =
        STICKY_ENTRY_STATE_KEYS.some((key) => prevItem[key] !== state[key]) ||
        prevItem.direction !== options.direction ||
        prevItem.offset !== options.offset ||
        prevItem.disableBottom !== options.disableBottom ||
        prevItem.type !== options.type ||
        prevItem.shouldInfluenceScroll !==
          (options.shouldInfluenceScroll ?? true);
      // offsetRange is omitted from diff calculation, as it can trigger
      // unnecessary updates
      if (!hasChanged) {
        // Nothing has changed; do nothing
        return prevState;
      }
      if (prevItem.direction !== options.direction) {
        // The sticky's direction has changed - re-sort the set
        shouldSortEntriesRef.current = true;
      }
      // Overwrite the related value
      return {
        ...prevState,
        [id]: {
          ...prevItem,
          direction: options.direction,
          disabled: state.disabled,
          emulatedDisabled: state.emulatedDisabled,
          height: state.height,
          bottom: state.bottom,
          beginY: state.beginY,
          offset: options.offset,
          offsetRange: options.offsetRange,
          disableBottom: options.disableBottom,
          type: options.type,
          shouldInfluenceScroll: options.shouldInfluenceScroll ?? true,
        },
      };
    });
  }, [updateEntries]);
  const handleUnregister = useCallback((id: string): void => {
    shouldSortEntriesRef.current = true;
    updateEntries((prevState) => {
      const newState = { ...prevState };
      delete newState[id];
      return newState;
    });
  }, [updateEntries]);
  const handleGetState = useCallback((): StickyValueContextValue => {
    return { entries: entriesRef.current };
  }, []);
  const controlValue = useMemo(() => ({
    register: handleRegister,
    update: handleUpdate,
    unregister: handleUnregister,
    getState: handleGetState,
  }), [handleRegister, handleUpdate, handleUnregister, handleGetState]);
  return (
    <StickyControlContext.Provider value={controlValue}>
      <StickyValueContext.Provider value={{ entries }}>
        { children }
      </StickyValueContext.Provider>
    </StickyControlContext.Provider>
  );
}
